/**
 * Lightweight HTML sanitizer used to defend against the stored / reflected
 * XSS vectors flagged in the BytePad security review.
 *
 * This is intentionally narrow — it removes `<script>`, event handlers
 * (`on*=...`), `javascript:` / `data:text/html` URLs, and other obvious sinks.
 * It is NOT a replacement for DOMPurify; once the team is ready to add a
 * dependency, prefer that. Until then this closes the most common holes
 * for `dangerouslySetInnerHTML` / `v-html` usage in our trees.
 *
 * Two sanitization modes are exposed:
 *   - sanitizeHtml(input): best-effort HTML pass-through with dangerous
 *     bits removed. Use when the value is "rich text" the user expects to
 *     see formatted (e.g. notification body, markdown render output).
 *   - escapeHtml(input): strict text-context escape. Use when the value is
 *     plain text and should never produce live DOM.
 */

const ENTITY_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

export const escapeHtml = (input: unknown): string => {
  if (input === null || input === undefined) return '';
  return String(input).replace(/[&<>"'`=/]/g, (ch) => ENTITY_MAP[ch] ?? ch);
};

const TAG_BLOCKLIST = [
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'svg',
  'math',
];

const blockTagRegex = new RegExp(
  `<\\s*\\/?\\s*(?:${TAG_BLOCKLIST.join('|')})\\b[^>]*>`,
  'gi'
);

const blockTagWithBodyRegex = new RegExp(
  `<\\s*(${TAG_BLOCKLIST.join('|')})\\b[^>]*>[\\s\\S]*?<\\s*\\/\\s*\\1\\s*>`,
  'gi'
);

const eventHandlerAttrRegex = /\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;

const dangerousUrlRegex =
  /(href|src|action|formaction|xlink:href|background|poster|data)\s*=\s*("|')\s*(javascript|vbscript|data:text\/html|data:application\/javascript)/gi;

const controlCharRegex = /[\u0000-\u001F\u007F-\u009F]/g;

export const sanitizeHtml = (input: unknown): string => {
  if (input === null || input === undefined) return '';
  let html = String(input);

  // Drop control chars (NUL, etc.) entirely.
  html = html.replace(controlCharRegex, '');

  // Strip blocklisted tags WITH bodies (e.g. <script>...</script>).
  html = html.replace(blockTagWithBodyRegex, '');
  // Then any orphaned opening / closing tag.
  html = html.replace(blockTagRegex, '');

  // Strip inline event handlers ( onclick=, onerror=, ... ).
  html = html.replace(eventHandlerAttrRegex, '');

  // Defang dangerous URL schemes on common attributes.
  html = html.replace(dangerousUrlRegex, '$1=$2#blocked');

  return html;
};

export default sanitizeHtml;
